import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PasswordModal } from '../components/case-study/PasswordModal';
import { profile } from '../data/profile';
type CaseStudyAccessValue = {
  isUnlocked: boolean;
  verify: (password: string) => boolean;
  grantAccess: () => void;
  requestAccess: (target: string) => void;
};
const STORAGE_KEY = 'kd-case-study-access';
const CaseStudyAccessContext = createContext<CaseStudyAccessValue | null>(null);
export function CaseStudyAccessProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const [isUnlocked, setIsUnlocked] = useState(() => readStoredAccess());
  const [pendingTarget, setPendingTarget] = useState<string | null>(null);
  const verify = useCallback((password: string) => password.trim() === profile.caseStudyPassword, []);
  const grantAccess = useCallback(() => {
    setIsUnlocked(true);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, 'granted');
    } catch {
      // Storage unavailable (e.g. private mode); access lasts for this page session only.
    }
  }, []);
  const requestAccess = useCallback((target: string) => {
    if (isUnlocked) {
      navigate(target);
      return;
    }
    setPendingTarget(target);
  }, [isUnlocked, navigate]);
  const value = useMemo(() => ({
    isUnlocked,
    verify,
    grantAccess,
    requestAccess
  }), [isUnlocked, verify, grantAccess, requestAccess]);
  return <CaseStudyAccessContext.Provider value={value} data-id="element-674" data-mp="11a5w4ka0lolo">
      {children}
      <PasswordModal open={pendingTarget !== null} onClose={() => setPendingTarget(null)} onSuccess={() => {
      const target = pendingTarget;
      setPendingTarget(null);
      if (target) navigate(target);
    }} data-id="element-675" data-mp="167xy674dir3l" />
    </CaseStudyAccessContext.Provider>;
}
export function useCaseStudyAccess() {
  const ctx = useContext(CaseStudyAccessContext);
  if (!ctx) throw new Error('useCaseStudyAccess must be used within CaseStudyAccessProvider');
  return ctx;
}
function readStoredAccess() {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === 'granted';
  } catch {
    return false;
  }
}